import { useMemo } from 'react'

import {
  Box,
  Center,
  Divider,
  Flex,
  HStack,
  Link,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@chakra-ui/react'
import { useLocation, useNavigate, Link as RRLink } from 'react-router'
// eslint-disable-next-line no-restricted-imports -- 新しいアイコンに移行する
import { PaginationNext } from 'src/components/__legacy__/icon/monochrome'
import { Paths } from 'src/constants/paths'
import { httpStatus } from 'src/constants/statusCodes'
import {
  useAuthenticatedAccount,
  usePermission,
} from 'src/features/auth/context'
import { usePatient } from 'src/features/patient/api'
import { PatientDetailBox } from 'src/features/patient/components'
import { WorksheetListAccordion } from 'src/features/worksheet/components'
import { colors } from 'src/lib/chakra-theme/foundations/colors'
import { WorksheetStatus } from 'src/lib/gql-client/client-preset/graphql'
import { PERMISSIONS } from 'src/lib/permission'

type Props = {
  patientUid: string
  children: React.ReactNode
}

const documentExchangeNeedActionStatuses = (
  role: 'Main' | 'Partner' | null,
): WorksheetStatus[] => {
  if (role === 'Main') {
    return ['DECreated', 'DEPresend']
  }
  if (role === 'Partner') {
    return ['DEAwaitingReceipt']
  }
  return []
}

export const PatientDetailLayout: React.FC<Props> = ({
  patientUid,
  children,
}) => {
  const location = useLocation()

  const selectedWorksheetUid = location.pathname.includes('worksheets')
    ? (location.pathname.split('/').pop() ?? null)
    : null

  const {
    account: { selectedTrialHospitalUid, selectedTrialHospitalRole },
  } = useAuthenticatedAccount()

  const { hasPermission } = usePermission()

  const navigate = useNavigate()

  const { data: patient } = usePatient({
    patientUid,
    revalidateIfStale: false,
    onSuccess: patient => {
      if (hasPermission(PERMISSIONS.Hospital_BelongAll)) {
        return
      }
      // 参照権限はあっても選択している医療機関で閲覧権限がない場合はDashboardにリダイレクトする
      if (
        patient.trialHospital.uid !== selectedTrialHospitalUid &&
        patient.partnerTrialHospital?.uid !== selectedTrialHospitalUid
      ) {
        navigate(Paths.Dashboard)
      }
    },
    onError: error => {
      if (error.status === httpStatus.Forbidden) {
        // 患者の参照権限がない場合 Dashboardにリダイレクトする（患者一覧に戻す）
        navigate(Paths.Dashboard)
      }
      console.error(error)
    },
  })

  const needActionFollowUpCount = useMemo(() => {
    if (!patient) return 0
    const worksheets = patient.observationFollowUps.flatMap(
      followUp => followUp.worksheets,
    )
    const needActionWorksheets = worksheets.filter(
      w =>
        w.latestWorksheetLog.status === 'Created' ||
        w.latestWorksheetLog.status === 'Saved',
    )
    return needActionWorksheets.length
  }, [patient])

  const needActionDocumentExchangeCount = useMemo(() => {
    if (!patient) return 0
    const worksheets = patient.observationDocumentExchanges.flatMap(
      documentExchange => documentExchange.worksheets,
    )
    const needActionWorksheets = worksheets.filter(w =>
      documentExchangeNeedActionStatuses(selectedTrialHospitalRole).includes(
        w.latestWorksheetLog.status,
      ),
    )
    return needActionWorksheets.length
  }, [patient, selectedTrialHospitalRole])

  const isFollowUp = useMemo(() => {
    if (!patient) return false
    return patient.observationFollowUps.some(f =>
      f.worksheets.some(w => w.worksheetUid === selectedWorksheetUid),
    )
  }, [patient, selectedWorksheetUid])

  const isDocumentExchange = useMemo(() => {
    if (!patient) return false
    return patient.observationDocumentExchanges.some(de =>
      de.worksheets.some(w => w.worksheetUid === selectedWorksheetUid),
    )
  }, [patient, selectedWorksheetUid])

  if (!patient) return null

  return (
    <Flex h="full">
      {/* 2column目 */}
      <Flex w="320px" h="full" direction="column">
        <HStack p="4" pb="2" align="center">
          <Link
            as={RRLink}
            to="../"
            color="blue.500"
            fontSize="sm"
            fontWeight="bold"
          >
            登録患者一覧
          </Link>
          <PaginationNext size="M" color={colors.gray[500]} />
          <Text fontSize="sm" fontWeight="bold">
            登録患者情報
          </Text>
        </HStack>
        <PatientDetailBox patientUid={patientUid} />

        <Tabs
          defaultIndex={isDocumentExchange ? 2 : isFollowUp ? 1 : 0}
          flex="1"
          overflow="auto"
        >
          <TabList mx="4">
            <Tab>Visit</Tab>
            <Tab position="relative">
              {needActionFollowUpCount > 0 && (
                <Center
                  position="absolute"
                  right="0"
                  top="0"
                  w="4"
                  h="4"
                  borderRadius="full"
                  bg="red.500"
                  color="white"
                  fontSize="xs"
                >
                  {needActionFollowUpCount}
                </Center>
              )}
              随時観察
            </Tab>
            <Tab position="relative">
              {needActionDocumentExchangeCount > 0 && (
                <Center
                  position="absolute"
                  right="0"
                  top="0"
                  w="4"
                  h="4"
                  borderRadius="full"
                  bg="red.500"
                  color="white"
                  fontSize="xs"
                >
                  {needActionDocumentExchangeCount}
                </Center>
              )}
              指示
            </Tab>
          </TabList>
          <TabPanels pt="2" overflowY="scroll">
            <TabPanel p="0">
              <WorksheetListAccordion
                observationType="Visit"
                patientUid={patientUid}
                selectedWorksheetUid={selectedWorksheetUid}
              />
            </TabPanel>
            <TabPanel p="0">
              <WorksheetListAccordion
                observationType="FollowUp"
                patientUid={patientUid}
                selectedWorksheetUid={selectedWorksheetUid}
              />
            </TabPanel>
            <TabPanel p="0">
              <WorksheetListAccordion
                observationType="DocumentExchange"
                patientUid={patientUid}
                selectedWorksheetUid={selectedWorksheetUid}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Flex>

      <Divider orientation="vertical" />

      {/* メイン部分 */}
      <Box flex="1" overflow="auto">
        {children}
      </Box>
    </Flex>
  )
}
